import React from "react";
import SeoPage from "../../components/SeoPage";
import RELATED from "../../data/related";
import { faqsByTopic } from "../../data/faqs";

export default function ImportExportCsv() {
    return (
        <SeoPage
            title="Import & Export CSV — Move Data Between Any Platforms"
            description="The complete guide to importing and exporting CSV files between Shopify, Excel, Mailchimp, your CRM, and any other platform that uses CSV."
            breadcrumbs={[{ label: "Home", href: "/" }, { label: "Import & Export CSV" }]}
            eyebrow="/ how to"
            h1="How to import and export CSV files"
            heroLead="Almost every web tool has an 'Export as CSV' or 'Import from CSV' button. Here's how to use them well, what to fix between platforms, and how to avoid the gotchas."
            sections={[
                { heading: "The basic workflow",
                    bullets: [
                        "Export the CSV from the source platform",
                        "Open it in OpenCSV to preview and edit",
                        "Clean it (deduplicate, fix headers, strip unused columns)",
                        "Download the cleaned CSV",
                        "Import into the destination platform",
                    ],
                },
                { heading: "Common platforms — what to know",
                    sub: [
                        { heading: "Shopify", body: "Products export with very specific column headers (Handle, Title, Body HTML, Vendor, Type, Tags, etc.). Don't rename columns; Shopify is strict. Edit values only." },
                        { heading: "Mailchimp / Klaviyo", body: "Audience exports include subscriber status. Strip out 'unsubscribed' and 'cleaned' rows before importing into a new tool, or you'll re-mail people who opted out." },
                        { heading: "Stripe", body: "Customer and payment exports use UTC timestamps in ISO 8601 format. If you reimport these somewhere else, make sure the destination tool understands UTC." },
                        { heading: "Salesforce / HubSpot", body: "CRM imports usually require a unique-ID column. Make sure your CSV has one (email is a common choice) before importing." },
                        { heading: "Excel / Google Sheets", body: "Both will happily eat a CSV — but be careful saving back. Both can convert columns silently. If you only need to view or do simple edits, use OpenCSV instead." },
                    ],
                },
                { heading: "Three classic gotchas",
                    sub: [
                        { heading: "Encoding", body: "If your source data has accented characters or emoji, save the CSV as UTF-8. Otherwise non-ASCII characters arrive at the destination as garbage (\"Café\" → \"CafÃ©\"). OpenCSV defaults to UTF-8." },
                        { heading: "Quoted fields with commas", body: "If a value contains a comma (like an address), it must be wrapped in double-quotes. Most tools handle this automatically — but bad CSV exports sometimes break it. Open in a text editor to verify if rows look mis-aligned." },
                        { heading: "Hidden line breaks", body: "A description field with newlines inside a single cell can break naive parsers. OpenCSV handles them; some platforms don't. If you see broken rows after import, search for line breaks in long-text columns and replace with spaces." },
                    ],
                },
                { heading: "Pre-import checklist",
                    bullets: [
                        "Headers match what the destination expects",
                        "No duplicate rows on the unique-ID column",
                        "Encoding is UTF-8",
                        "No trailing empty rows",
                        "Column count is consistent across rows",
                        "Numbers, dates, and IDs aren't accidentally formatted",
                    ],
                },
            ]}
            faqs={faqsByTopic("import-export", "general", "delimiters")}
            related={[RELATED.viewer, RELATED.editor, RELATED.use, RELATED.what, RELATED.vs, RELATED.faq]}
        />
    );
}
